import React from 'react'
import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import {
    ClipboardList,
    FileText,
    Play,
    Eye,
    Trash2,
    CheckCircle2,
    Clock,
    Building2,
    BarChart3,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { AuthorizationGate } from '@/features/authorization/AuthorizationGate'
import { useAuthorization } from '@/features/authorization/AuthorizationContext'
import type { Assessment } from '@/features/assessments/types'

// ── Status Badge ──

function StatusBadge({ status }: { status: string }) {
    const config: Record<string, { label: string; icon: React.ReactNode; className: string }> = {
        completed: {
            label: 'Completed',
            icon: <CheckCircle2 className="h-3 w-3" />,
            className: 'bg-green-100 text-green-700',
        },
        in_progress: {
            label: 'In Progress',
            icon: <Clock className="h-3 w-3" />,
            className: 'bg-amber-100 text-amber-700',
        },
        draft: {
            label: 'Draft',
            icon: <FileText className="h-3 w-3" />,
            className: 'bg-gray-100 text-gray-700',
        },
    }
    const c = config[status] || config.draft
    return (
        <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${c.className}`}>
            {c.icon} {c.label}
        </span>
    )
}

// ── Main Card (Full-Width Row) ──

interface AssessmentCardProps {
    assessment: Assessment
    index?: number
    onDelete?: (a: Assessment) => void
    onStart?: (a: Assessment) => void
}

export function AssessmentCard({ assessment, index, onDelete, onStart }: AssessmentCardProps) {
    const { hasAction } = useAuthorization()
    const canManage = assessment.is_creator || hasAction('manage_assessments')

    return (
        <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, delay: (index ?? 0) * 0.06, ease: [0.25, 0.46, 0.45, 0.94] }}
        >
            <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg bg-white hover:shadow-sm transition-shadow">
                {/* Left — Icon + Info */}
                <div className="flex items-center gap-4 min-w-0 flex-1">
                    <div className="h-10 w-10 rounded-lg bg-indigo-100 flex items-center justify-center shrink-0">
                        <ClipboardList className="h-5 w-5 text-indigo-600" />
                    </div>
                    <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold text-gray-900 truncate">{assessment.name}</p>
                        <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                            <StatusBadge status={assessment.status} />
                            {assessment.organization_name && (
                                <span className="inline-flex items-center gap-1 text-xs text-gray-500">
                                    <Building2 className="h-3 w-3" />
                                    {assessment.organization_name}
                                </span>
                            )}
                            {assessment.answer_count != null && assessment.answer_count > 0 && (
                                <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-blue-50 text-blue-700">
                                    {assessment.answer_count} answers
                                </span>
                            )}
                            <span className="text-xs text-gray-400">
                                Created {new Date(assessment.created_at).toLocaleDateString()}
                            </span>
                            {assessment.created_by_username && (
                                <span className="text-xs text-gray-400">
                                    by {assessment.created_by_username}
                                </span>
                            )}
                        </div>
                    </div>
                </div>

                {/* Right — Action Buttons */}
                <div className="flex items-center gap-2 shrink-0 ml-4">
                    {assessment.status === 'draft' && onStart && canManage && (
                        <AuthorizationGate actions={['update_assessment']}>
                            <Button variant="outline" size="sm" onClick={() => onStart(assessment)}>
                                <Play className="h-3.5 w-3.5 mr-1" />
                                Start
                            </Button>
                        </AuthorizationGate>
                    )}
                    {assessment.status === 'draft' && !onStart && (
                        <Button variant="outline" size="sm" asChild>
                            <Link to={`/assessments/${assessment.id}/questionnaire${canManage ? '' : '?readonly=true'}`}>
                                <Eye className="h-3.5 w-3.5 mr-1" />
                                View
                            </Link>
                        </Button>
                    )}
                    {assessment.status === 'in_progress' && (
                        <Button variant="outline" size="sm" asChild>
                            <Link to={`/assessments/${assessment.id}/questionnaire${canManage ? '' : '?readonly=true'}`}>
                                {canManage ? <Play className="h-3.5 w-3.5 mr-1" /> : <Eye className="h-3.5 w-3.5 mr-1" />}
                                {canManage ? 'Continue' : 'View'}
                            </Link>
                        </Button>
                    )}
                    {assessment.status === 'completed' && (
                        <>
                            <Button variant="outline" size="sm" asChild>
                                <Link to={`/assessments/${assessment.id}/questionnaire?readonly=true`}>
                                    <Eye className="h-3.5 w-3.5 mr-1" />
                                    View
                                </Link>
                            </Button>
                            <AuthorizationGate actions={['view_results']}>
                                <Button variant="outline" size="sm" asChild>
                                    <Link to={`/assessments/${assessment.id}/results`}>
                                        <BarChart3 className="h-3.5 w-3.5 mr-1" />
                                        Results
                                    </Link>
                                </Button>
                            </AuthorizationGate>
                        </>
                    )}
                    {canManage && (
                        <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                            onClick={() => onDelete?.(assessment)}
                        >
                            <Trash2 className="h-4 w-4" />
                        </Button>
                    )}
                </div>
            </div>
        </motion.div>
    )
}
